import { useCallback, useEffect, useMemo, useState } from 'react';
import Plausible from 'plausible-tracker';
import TagManager from 'react-gtm-module';

import { MatomoTracker } from '@canvasflow/analytics';

import { TrackingContext, LOCATION_CHANNEL_KEY } from './Tracking.context';
import config from '../config';
import { TrackingEvent, TrackerLogger } from './Tracking.events';
import { Trackers } from './Trackers';
import { MetaPixelTracker } from './trackers/MetaPixelTracker';
import { APP_USER_ID } from '@/constants';
import { TiktokPixelTracker } from './trackers/TiktokPixelTracker';
import { SegmentTracker, SEGMENT_WRITE_KEY } from './trackers/SegmentTracker';

const ANALYTICS_ENABLED_KEY = 'analytics_enabled';
const ANALYTICS_CHANNEL_KEY = 'analytics';
const ANALYTICS_ENABLED = 'enable';
const ANALYTICS_DISABLE = 'disable';

declare global {
	const OnetrustActiveGroups: any;
	const a360_targetting_cookies: any;
	interface Window {
		a360_targetting_cookies: any;
	}
	interface Window {
		gtag: any;
		_paq: [string, ...any[]][];
	}
}

export function Tracking({ children }: TrackingProps) {
	const { analytics } = config;
	const [gtag, setGtag] = useState(null);
	const [gtm, setGTM] = useState(null);
	const [plausible, setPlausible] = useState(null);
	const [matomo, setMatomo] = useState(null);
	const [metaPixel, setMetaPixel] = useState(null);
	const [tiktokPixel, setTiktokPixel] = useState(null);
	const [segment, setSegment] = useState(null);
	const [locationChannel] = useState(
		new BroadcastChannel(LOCATION_CHANNEL_KEY),
	);
	const [analyticsChannel] = useState(
		new BroadcastChannel(ANALYTICS_CHANNEL_KEY),
	);

	const trackerOptions = {
		analytics,
		setGtag,
		setGTM,
		setMatomo,
		setPlausible,
		setMetaPixel,
		setTiktokPixel,
		setSegment,
	};
	useEffect(() => {
		initializeTrackers(trackerOptions);
		// TODO this is only aplicable for A360
		// if (localStorage.getItem(ANALYTICS_ENABLED_KEY)) {
		// 	initializeTrackers(trackerOptions);
		// }
		analyticsChannel.onmessage = ({ data }) => {
			if (
				data === ANALYTICS_DISABLE &&
				localStorage.getItem(ANALYTICS_ENABLED_KEY)
			) {
				disableTrackers(trackerOptions);
				// location.reload();
				return;
			}
			if (
				data === ANALYTICS_ENABLED &&
				!localStorage.getItem(ANALYTICS_ENABLED_KEY)
			) {
				initializeTrackers(trackerOptions);
				// location.reload();
				return;
			}
		};
	}, []);

	const trackers: Trackers = useMemo(() => {
		return new Trackers({
			gtag,
			gtm,
			matomo,
			plausible,
			metaPixel,
			tiktokPixel,
			segment,
		});
	}, [gtag, gtm, matomo, plausible, metaPixel, tiktokPixel, segment]);

	const sendEvent = useCallback(
		(event: TrackingEvent) => {
			trackers.sendEvent(event);
			analyticsChannel.postMessage(event);
		},
		[trackers],
	);

	return (
		<TrackingContext.Provider
			value={{
				gtag,
				gtm,
				plausible,
				locationChannel,
				matomo,
				sendEvent,
				metaPixel,
				tiktokPixel,
				segment,
			}}
		>
			{children}
		</TrackingContext.Provider>
	);
}

function initializeTrackers(args: TrackersOptions) {
	const {
		analytics,
		setGtag,
		setGTM,
		setPlausible,
		setMatomo,
		setMetaPixel,
		setTiktokPixel,
		setSegment,
	} = args;
	TrackerLogger.info(`Initialize trackers: \n`, analytics);
	if (analytics?.gtag?.trackerID) {
		initializeGtag(setGtag);
	}

	if (analytics?.gtm?.trackerID) {
		initializeGTM(setGTM);
	}

	if (analytics?.plausible?.domain) {
		initializePlausible(setPlausible);
	}

	if (analytics?.metaPixel?.trackerID) {
		initializeMetaPixel(setMetaPixel);
	}

	if (analytics?.metaPixel?.trackerID) {
		initializeTiktokPixel(setTiktokPixel);
	}

	if (analytics?.matomo?.srcUrl && analytics?.matomo?.urlBase) {
		initializeMatomo(setMatomo);
	}

	// TODO: Gate on `analytics?.segment?.writeKey` once the write key is
	// provided through the configuration object. For now it falls back to the
	// hardcoded key in SegmentTracker.
	initializeSegment(setSegment);
	localStorage.setItem(ANALYTICS_ENABLED_KEY, 'true');
}

function disableTrackers(args: TrackersOptions) {
	const { setGtag, setGTM, setMatomo, setPlausible } = args;
	TrackerLogger.info(`I disable all the analytics`);
	setGtag(null);
	setGTM(null);
	setMatomo(null);
	setPlausible(null);
	localStorage.removeItem(ANALYTICS_ENABLED_KEY);
}

interface TrackersOptions {
	analytics: any;
	setGtag: (opts: any) => void;
	setGTM: (opts: any) => void;
	setMatomo: (opts: any) => void;
	setPlausible: (opts: any) => void;
	setMetaPixel: (opts: any) => void;
	setTiktokPixel: (opts: any) => void;
	setSegment: (opts: any) => void;
}

interface TrackingProps {
	children: string | JSX.Element | JSX.Element[];
}

function initializeGtag(setter: (gtag: any) => void) {
	/*let sampleRate = 1;
	if (
		!!analytics.ga4.sampleRate &&
		typeof analytics.ga4.sampleRate === 'number'
	) {
		if (analytics.ga4.sampleRate > 0 && analytics.ga4.sampleRate < 100) {
			sampleRate = analytics.ga4.sampleRate;
		}
	}*/

	if (window.gtag) {
		setter(true);
		return;
	}

	const interval = setInterval(() => {
		if (window.gtag) {
			clearInterval(interval);
			return;
		}

		setter(true);
	}, 500);
}

function initializeGTM(setter: (gtm: any) => void) {
	const { analytics } = config;

	const tagManagerArgs = {
		gtmId: analytics?.gtm?.trackerID,
	};

	TagManager.initialize(tagManagerArgs);
	console.log(`TAG MANAGER:`, TagManager);
	setter(TagManager);
	return;
}

function initializePlausible(setter: (plausible: any) => void) {
	const { analytics } = config;
	const domain = analytics?.plausible?.domain;

	const plausible = Plausible({
		domain: domain,
		trackLocalhost: true,
	});

	setter(plausible);
	return;
}

function initializeMetaPixel(setter: (metaPixel: any) => void) {
	const { analytics } = config;
	const pixelId = analytics?.metaPixel?.trackerID;
	if (!pixelId) return;

	MetaPixelTracker.init(pixelId);

	setter(pixelId);
	return;
}

function initializeTiktokPixel(setter: (tiktokPixel: any) => void) {
	const { analytics } = config;
	const pixelId = analytics?.tiktokPixel?.trackerID;
	if (!pixelId) return;

	TiktokPixelTracker.init(pixelId);

	setter(pixelId);
	return;
}

function initializeSegment(setter: (segment: any) => void) {
	const { analytics } = config;
	const writeKey = analytics?.segment?.writeKey || SEGMENT_WRITE_KEY;
	if (!writeKey) return;

	SegmentTracker.init(writeKey);

	setter(writeKey);
	return;
}

function initializeMatomo(setter: (matomo: any) => void) {
	const userId = localStorage.getItem(APP_USER_ID) || undefined;
	const { analytics } = config;
	const {
		urlBase,
		siteId,
		trackerUrl,
		srcUrl,
		disabled,
		heartBeat,
		linkTracking = true,
		configurations = {},
	} = analytics.matomo;

	const matomo = new MatomoTracker({
		urlBase,
		siteId,
		trackerUrl,
		srcUrl,
		disabled,
		heartBeat,
		linkTracking,
		configurations,
		userId,
	});

	setter(matomo);
}
